/**
 * Archived Children Tab Component
 * Tab content for viewing and managing archived children
 */
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { AlertTriangle, Trash2, RefreshCw } from "lucide-react";
import { DeletedChildCard } from "./DeletedChildCard";

export const DeletedChildrenTab = ({
  children,
  onRestore,
  onPermanentDelete,
  selectMode,
  selectedChildren,
  onToggleSelect,
  onBulkRestore,
  onBulkPermanentDelete,
  selectedCount,
  onClearAll,
  tabLabel = "Archived",
  canEdit = true
}) => {
  if (children.length === 0) {
    return (
      <Card className="bg-red-50/80 backdrop-blur-sm border-2 border-red-200">
        <CardContent className="p-8 text-center">
          <p className="text-red-600">No {tabLabel.toLowerCase()} children</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {/* Warning Banner with Clear All Button */}
      <Card className="bg-red-100 border-red-300">
        <CardContent className="p-4">
          <div className="flex items-start justify-between">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-red-600 mt-0.5" />
              <div>
                <p className="font-semibold text-red-800">{tabLabel} Children ({children.length})</p>
                <p className="text-sm text-red-700 mt-1">
                  {canEdit 
                    ? "These children have been archived. You can re-establish them or permanently delete them. Permanently deleted children and their history cannot be recovered."
                    : "These children have been archived. Contact owner to restore or delete them."
                  }
                </p>
              </div>
            </div>
            {canEdit && onClearAll && (
              <Button
                onClick={onClearAll}
                variant="destructive"
                size="sm"
                className="bg-red-600 hover:bg-red-700 text-white"
                data-testid="clear-all-archived-btn"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Clear All
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Bulk Actions (when items selected) - Only for owners/co-owners */}
      {canEdit && selectMode && selectedCount > 0 && (
        <div className="flex gap-2 p-4 bg-red-50 rounded-lg border border-red-200">
          <Button
            onClick={onBulkRestore}
            variant="outline"
            className="border-green-300 text-green-700 hover:bg-green-50"
            data-testid="bulk-restore-btn"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Restore Selected ({selectedCount})
          </Button>
          <Button
            onClick={onBulkPermanentDelete}
            variant="outline"
            className="border-red-500 text-red-700 hover:bg-red-100"
            data-testid="bulk-permanent-delete-btn"
          >
            <Trash2 className="w-4 h-4 mr-2" />
            Permanently Delete ({selectedCount})
          </Button>
        </div>
      )}

      {/* Archived Children List */}
      <div className="grid gap-4">
        {children.map((child) => (
          <DeletedChildCard
            key={child.id}
            child={child}
            onRestore={onRestore}
            onPermanentDelete={onPermanentDelete}
            selectMode={canEdit && selectMode}
            isSelected={selectedChildren.has(child.id)}
            onToggleSelect={onToggleSelect}
            canEdit={canEdit}
          />
        ))}
      </div>
    </div>
  );
};

export default DeletedChildrenTab;
